import { useMissionControlStore } from '../store.js';
import type { ApprovalRequest, Receipt } from '../types.js';

function formatCost(value: number): string {
  return `$${value.toFixed(2)}`;
}

export function ApprovalQueue({
  approvals,
  receipts,
}: {
  approvals: ApprovalRequest[];
  receipts: Receipt[];
}) {
  const resolveApproval = useMissionControlStore((s) => s.resolveApproval);
  const emitEvent = useMissionControlStore((s) => s.emitEvent);
  const addReceipt = useMissionControlStore((s) => s.addReceipt);
  const lanes = useMissionControlStore((s) => s.lanes);

  function handleResolve(request: ApprovalRequest, approved: boolean) {
    resolveApproval(request.id, approved);
    emitEvent({
      workspaceId: request.workspaceId,
      laneId: request.laneId,
      kind: 'approval_resolve',
      summary: `${approved ? 'Approved' : 'Rejected'}: ${request.title}`,
    });
    const lane = lanes.find((l) => l.id === request.laneId);
    addReceipt({
      workspaceId: request.workspaceId,
      laneId: request.laneId,
      goal: request.title,
      outcome: approved ? 'completed' : 'rejected',
      entries: lane?.logs.length ?? 0,
      signed: true,
      cost: 0.04,
    });
  }

  return (
    <section className="panel approval-queue" aria-label="Approval queue">
      <div className="section-heading">
        <span className="section-kicker">Approvals</span>
        <strong>
          {approvals.length === 0
            ? 'Queue clear'
            : `${approvals.length} gate${approvals.length === 1 ? '' : 's'} waiting`}
        </strong>
      </div>

      {approvals.length === 0 ? (
        <p className="approval-empty">Nothing pending. Signed receipts land here when work completes.</p>
      ) : (
        <div className="approval-list">
          {approvals.map((request) => (
            <article key={request.id} className={`approval-card risk-${request.risk}`}>
              <div className="approval-row">
                <strong>{request.title}</strong>
                <span className={`risk-pill risk-${request.risk}`}>{request.risk}</span>
              </div>
              <p className="approval-summary">{request.summary}</p>
              {request.command ? <pre className="approval-command">{request.command}</pre> : null}
              {request.diff ? <pre className="approval-diff">{request.diff}</pre> : null}
              <div className="approval-actions">
                <button
                  className="primary-button"
                  type="button"
                  onClick={() => handleResolve(request, true)}
                  aria-label={`Approve ${request.title}`}
                >
                  Approve
                </button>
                <button
                  className="ghost-button"
                  type="button"
                  onClick={() => handleResolve(request, false)}
                  aria-label={`Reject ${request.title}`}
                >
                  Reject
                </button>
              </div>
            </article>
          ))}
        </div>
      )}

      {receipts.length > 0 ? (
        <div className="receipt-strip">
          <span className="section-kicker">Signed receipts</span>
          <ol className="receipt-list">
            {receipts.slice(0, 5).map((receipt) => (
              <li key={receipt.id} className={`receipt-item receipt-${receipt.outcome}`}>
                <strong>{receipt.goal.slice(0, 42)}</strong>
                <span>
                  {receipt.outcome} · {receipt.entries} entries · {formatCost(receipt.cost)}
                </span>
                {receipt.signed ? <span className="receipt-signed">signed</span> : null}
              </li>
            ))}
          </ol>
        </div>
      ) : null}
    </section>
  );
}
